<template>
  <div class="map" id="map">
    <div v-if="this.$store.getters['isComparisonModeActive']" class="divider" :style="{ 'left': dividerOffset + 'px' }"></div>
    <tooltip-component v-if="tooltip" :layer="tileSetLayer" />
    <button v-if="$conf.tilesets.layers.length > 0 && $conf.bordersToggler.enabled === true"
      class="button is-dark has-background-black-bis border-toggler"
      type="button"
      style=""
      position="is-bottom-left"
      @click="toggleBorders">
        <b-icon v-if="borders" id="layers-outline" icon="layers-outline"></b-icon>
        <b-icon v-if="!borders" id="layers-off-outline" icon="layers-off-outline"></b-icon>
    </button>
    <b-slider v-if="this.$store.getters['isComparisonModeActive']" id="comparisonSwipe" @input="listener" v-model="overlap" :step="0.1" :tooltip="false" style="width: 100%; bottom:50%; z-index: 10; position:absolute;"/>
  </div>
</template>

<script>

import { createStyle } from 'util/layer/StyleBuilder'
import TooltipComponent from './Tooltip'
import { colorFromArray, deepCopy } from 'util/helpers'

/**
 * App component
 */
export default {
  name: 'MapComponent',
  components: {
    TooltipComponent
  },
  data () {
    return {
      dividerOffset: 0,
      layers: {},
      tileSetLayer: null,
      viewPadding: {
        left: 0,
        bottom: 0
      },
      selected: [],
      tooltip: false,
      timebarProperty: null,
      timebarValue: null,
      mapFilter: null,
      searchResultsIds: [],
      borders: this.$store.getters['config'].tilesets.toggled
    }
  },
  mounted () {
    this.layers.overlay = this.$map.createLayer({
      type: 'Group',
      layers: []
    })
    this.layers.base = this.$map.createLayer({
      type: 'Group',
      layers: []
    })
    this.layers.comparison = this.$map.createLayer({
      type: 'Group',
      layers: []
    })
    this.layers.tileSet = this.$map.createLayer({
      type: 'Group',
      layers: []
    })
    this.$store.dispatch('initializeLayersToCompareGroup', this.layers.comparison)

    this.createLayers(this.$conf.map.layers.baseLayers, this.$conf.map.baseLayer, 'base')
    this.createLayers(this.$conf.map.layers.overlays, this.$conf.map.overlayLayer, 'overlay')

    if (this.$store.getters['tileSetProperty']) {
      this.timebarProperty = this.$store.getters['tileSetProperty']
    }
    if (this.$store.getters['timebarValue']) {
      this.timebarValue = this.$store.getters['timebarValue']
    }
    if (this.$store.getters['tileSetConf']) {
      this.addTileSetLayer(this.$store.getters['tileSetConf'])
    }
    this.createMap()
    this.subscribe()
  },
  computed: {
    overlap: {
      get () {
        return this.$store.getters['comparisonOverlap']
      },
      set (overlap) {
        this.$store.commit('setComparisonOverlap', overlap)
      }
    }
  },
  methods: {
    listener () {
      // Set divider offset as a percent of overlap within map width and minus half of the divider width
      this.dividerOffset = this.overlap / 100 * this.$map.map.getSize()[0] - 1
      this.$map.map.on('moveend', () => {
        this.dividerOffset = this.overlap / 100 * this.$map.map.getSize()[0] - 1
      })
      this.$map.map.render()
    },
    toggleBorders () {
      if (this.borders) {
        this.tileSetLayer.setVisible(false)
        this.borders = false
      }
      else
      {
        this.tileSetLayer.setVisible(true)
        this.borders = true
      }
    },
    createMap () {
      this.$map.set({
        layers: Object.keys(this.layers).map(i => this.layers[i])
      })
    },
    createLayers (layers, activeLayer, type) {
      // Set up and create layers provided
      Object.keys(layers).forEach(id => {
        layers[id].id = id

        // Have requested raster layers turned off at first to avoid erroneous requests with no TIME parameter
        if (type === 'base' && id === activeLayer && this.$store.getters['mapTime'].length === 0) {
          layers[id].visible = false
        } else {
          layers[id].visible = (id === activeLayer)
        }

        // Add into corresponding groups
        const layer = this.$map.createLayer(layers[id])
        if (type === 'base') {
          this.$map.addLayer(layer, this.layers.base)
        } else if (type === 'overlay') {
          this.$map.addLayer(layer, this.layers.overlay)
        }
      })
    },
    changeBaseLayer (layerid) {
      if (layerid !== 'no_layer') {
        this.$store.dispatch('updateMapDatesOnLayerChange').then(() => {
          this.setLayerVisibility(layerid, 'base')
        }).catch(err => {
          console.log('error', err)
        })
      } else {
        //this.$store.dispatch('clearMapDatesOnLayerChange')
        this.setLayerVisibility(layerid, 'base')
      }
    },
    changeOverlayLayer (layerid) {
      this.setLayerVisibility(layerid, 'overlay')
    },
    setLayerVisibility (layerid, type) {
      this.layers[type].getLayers().forEach(layer => {
        if (layer.get('id') === layerid) {
          layer.setVisible(true)
        } else {
          layer.setVisible(false)
        }
      })
    },
    temporaryDisableBaseLayer (layerid, type) {
      this.layers[type].getLayers().forEach(layer => {
        if (layer.get('id') === layerid) {
          layer.setVisible(false)
        }
      })
    },
    // Change a layer source parameter (the paramater has to exist first).
    changeLayerParam (layer, param_name, param_value) {
      if (layer.getSource != null) {
        var layer_src = layer.getSource()
        if (layer_src != null && layer_src.getParams != null) {
          var params = layer_src.getParams()
          if (param_name in params) {
            var pd = {}
            pd[param_name] = param_value
            layer_src.updateParams(pd)
          }
        }
      }
    },
    // Change a source parameter for all layers.
    changeLayersParam (param_name, param_value) {
      // For each layer.
      this.layers.base.getLayers().forEach(layer => {
        if (layer != null) {
          // For groups, update sublayer source params, if any.
          if (layer.get('conf').type == 'Group') {
            for (var sublayer of layer.getLayersArray()) {
              this.changeLayerParam(sublayer, param_name, param_value)
            }
          }
          // Update layer source params, if any.
          this.changeLayerParam(layer, param_name, param_value)
        }
      })
    },
    subscribe () {
      // base layer change
      this.$store.watch(
        () => this.$store.getters['baseLayer'],
        layer => {
          if (layer) {
            this.changeBaseLayer(layer)
          }
        }
      )
      // overlay layer change
      this.$store.watch(
        () => this.$store.getters['overlayLayer'],
        layer => {
          if (layer) {
            this.changeOverlayLayer(layer)
          }
        }
      )
      // tileset change
      this.$store.watch(
        () => this.$store.getters['tileSetConf'],
        tileSetConf => {
          this.addTileSetLayer(tileSetConf)
        }
      )
      // property change
      this.$store.watch(
        () => this.$store.getters['tileSetProperty'],
        tileSetProperty => {
          this.timebarProperty = tileSetProperty
          if (this.tileSetLayer) {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // search results change
      this.$store.watch(
        () => this.$store.getters['searchResults'],
        results => {
          this.searchResultsIds = results.map(row => row[this.$conf.search[this.$store.getters['searchIndex']].id])
          this.selected = results.map(row => row[this.$conf.search[this.$store.getters['searchIndex']].id])
          this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
        }
      )
      // timebar value change
      this.$store.watch(
        () => this.$store.getters['timebarValue'],
        value => {
          this.timebarValue = value
          if (this.tileSetLayer) {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // filter change
      this.$store.watch(
        () => this.$store.getters['filter'],
        value => {
          this.mapFilter = value
          if (this.tileSetLayer) {
            this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
          }
        }
      )
      // mapTime change
      this.$store.watch(
        () => this.$store.getters['mapTime'],
        value => {
          if (value.length === 0) {
            this.temporaryDisableBaseLayer(this.$store.getters['baseLayer'], 'base')
          } else {
            this.setLayerVisibility(this.$store.getters['baseLayer'], 'base')
          }
          this.changeLayersParam('TIME', value)
        }
      )
      // comparison map mode change
      this.$store.watch(
        () => this.$store.getters['isComparisonModeActive'],
        status => {
          if (status === true) {
            this.temporaryDisableBaseLayer(this.$store.getters['baseLayer'], 'base')
          } else {
            this.setLayerVisibility(this.$store.getters['baseLayer'], 'base')
          }
        }
      )
      // click
      if (this.$conf.tilesets.select && this.$conf.tilesets.select.enabled) {
        this.$map.click({
          minZoom: this.$conf.tilesets.select.minZoom,
          maxZoom: this.$conf.tilesets.select.maxZoom,
          cb: (features, isShiftDown) => {
            if (features && features.length) {
              if (isShiftDown === false) {
                // Firt case when a single parcel is selected
                this.selected = []
                this.selected.push(features[0].get('id'))
              } else if (isShiftDown === true) {
                // Second case when shift is down for multiple parcels
                this.selected.push(features[0].get('id'))
              }

              // hack to get over search result component updated called twice
              this.$store.commit('parcel', null)
              
              // force redraw of layer style
              this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())

              this.$store.dispatch('search', {
                index: this.$conf.tilesets.select.searchIndex,
                query: this.selected
              })
              .then((result) => {
                this.mapSelect = false
                // force redraw of layer style
                this.tileSetLayer.setStyle(this.tileSetLayer.getStyle())
              })
              .catch(err => {
                this.mapSelect = false
                this.$conf.debug && console.error(err)
              })
            }
          }
        })
      }
    },
    addTileSetLayer (tileSetConf) {
      if (this.tileSetLayer) {
        this.removeTooltip(this.tileSetLayer)
        this.$map.removeLayer(this.tileSetLayer, this.layers.tileSet)
      }
      this.tileSetLayer = this.createTileSetLayer(tileSetConf)
      if (this.$conf.tilesets.zoomTo) {
        this.$map.fitTo({
          bbox: tileSetConf.bounds,
          srid: 'EPSG:4326'
        })
      }
      this.tileSetLayer.setVisible(this.borders)
      this.$map.addLayer(this.tileSetLayer, this.layers.tileSet)
      if (this.$conf.tilesets.select && this.$conf.tilesets.select.tooltip) {
        this.addTooltip(this.tileSetLayer)
      }
    },
    createTileSetLayer (conf) {
      const colorStyles = {
        default: createStyle(this.$conf.tilesets.style.default)
      }
      Object.keys(this.$conf.tilesets.colors).forEach((k, i) => {
        const color = colorFromArray(this.$conf.tilesets.colors[k])
        const styleConf = Object.assign({}, deepCopy(this.$conf.tilesets.style.default))
        styleConf.stroke.color = color
        styleConf.fill.color = color
        colorStyles[i] = createStyle(styleConf)
      })
      const searchResultsStyle = createStyle(this.$conf.tilesets.style.searchResults)
      const selectedStyle = createStyle(this.$conf.tilesets.style.selected)
      let labelStyle = null
      if (
        this.$conf.tilesets.style.labels &&
        this.$conf.tilesets.style.labels.enabled &&
        this.$conf.tilesets.style.labels.text
      ) {
        labelStyle = createStyle({
          geometry: feature => {
            const geometry = feature.getGeometry()
            if (geometry.getType() === 'MultiPolygon') {
              // Only render label for the widest polygon of a multipolygon
              const polygons = geometry.getPolygons()
              let widest = 0
              for (let i = 0, ii = polygons.length; i < ii; ++i) {
                const width = getWidth(polygons[i].getExtent())
                if (width > widest) {
                  widest = width
                  geometry = polygons[i]
                }
              }
            }
            return geometry
          },
          text: this.$conf.tilesets.style.labels.text
        })
      }
      const layerConf = {
        type: 'MVT',
        id: conf.id,
        title: 'Tile set',
        projection: 'EPSG:3857',
        url: this.$conf.tilesets.url + this.$conf.tilesets.key + '/' + conf.id + '/{z}/{x}/{y}.' + conf.format,
        extent: this.$map.transform({ bbox: conf.bounds, from: 'EPSG:4326' }),
        style: (feature, resolution, buildStyle) => {
          // select base style
          let baseStyle = 'default'
          let estimations = feature.get(this.timebarProperty)
          if (estimations) {
            estimations = estimations.split(';')
              .map(p => p.split(':'))
              .filter(pair => Number(pair[0]) <= this.timebarValue)
            if (estimations.length) {
              baseStyle = Number(estimations[estimations.length - 1][1])
            }
          }
          // filter
          if (baseStyle !== 'default' && this.mapFilter && baseStyle in this.mapFilter && this.mapFilter[baseStyle] === false) {
            return []
          }
          // fallback
          if (!(baseStyle in colorStyles)) {
            baseStyle = 'default'
          }
          const s = [colorStyles[baseStyle]]
          // search results
          if (this.searchResultsIds.length && this.searchResultsIds.indexOf(feature.get('id')) > -1) {
            s.push(searchResultsStyle)
          }
          // selected
          if (this.selected && this.selected.includes(feature.get('id'))) {
            s.push(selectedStyle)
            // set selected
            this.$store.dispatch('selectedFeature', feature)
          }
          // label
          if (this.$map.get().getView().getZoom() > this.$conf.tilesets.style.labels.minZoom && labelStyle) {
            labelStyle.getText().setText(feature.get('id'))
            s.push(labelStyle)
          }
          return s
        },
        tileGrid: {
          minZoom: conf.minzoom,
          maxZoom: conf.maxzoom
        }
      }
      return this.$map.createLayer(layerConf)
    },
    addTooltip (layer) {
      this.tooltip = true
    },
    removeTooltip (layer) {
      this.tooltip = false
    }
  }
}



</script>

<style lang="stylus">
.map
  height: 100%
  .ol-zoom
    left: auto
    right: .5em

.border-toggler
  position: absolute
  right: -3px
  z-index: 1
  top: 95px
  border-bottom-right-radius: 0
  border-top-right-radius: 0

#comparisonSwipe
  .b-slider-track
    height: 0
  .b-slider-thumb
    height: 3rem

.divider
  background: #e63946
  width: 2px
  height: 100vh
  position: absolute
  z-index: 10

</style>
